import React, { useEffect, useState, useCallback } from 'react';
import { FileText, Hash, Clock, DollarSign, ChevronDown, ChevronRight } from 'lucide-react';
import { promptsApi, type PromptVersionSummary, type PromptVersionDetail } from '@/services/api/agentApi';

interface AgentGroup {
  agentName: string;
  versions: PromptVersionSummary[];
  expanded: boolean;
}

export const PromptVersionsTab: React.FC = () => {
  const [groups, setGroups] = useState<AgentGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersion, setSelectedVersion] = useState<PromptVersionDetail | null>(null);

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await promptsApi.listVersions();
      const versions = res.data?.versions || [];

      // Group by agent name
      const agentMap = new Map<string, PromptVersionSummary[]>();
      for (const v of versions) {
        const list = agentMap.get(v.agentName) || [];
        list.push(v);
        agentMap.set(v.agentName, list);
      }

      setGroups(
        Array.from(agentMap.entries()).map(([agentName, vers]) => ({
          agentName,
          versions: vers.sort((a, b) => b.version - a.version),
          expanded: true,
        })),
      );
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVersions(); }, [fetchVersions]);

  const toggleGroup = (agentName: string) => {
    setGroups(prev => prev.map(g =>
      g.agentName === agentName ? { ...g, expanded: !g.expanded } : g,
    ));
  };

  const viewVersion = async (agentName: string, version: number) => {
    try {
      const res = await promptsApi.getVersion(agentName, version);
      setSelectedVersion(res.data as PromptVersionDetail);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  if (loading) {
    return <div className="p-6 text-muted-foreground">Loading prompt versions...</div>;
  }

  if (error) {
    return <div className="p-6 text-destructive">Error: {error}</div>;
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Prompt Versions</h3>
        <span className="text-sm text-muted-foreground">
          {groups.reduce((sum, g) => sum + g.versions.length, 0)} versions across {groups.length} agents
        </span>
      </div>

      {groups.map(group => (
        <div key={group.agentName} className="border border-border rounded-lg overflow-hidden">
          <button
            onClick={() => toggleGroup(group.agentName)}
            className="w-full flex items-center justify-between p-4 hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              {group.expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              <FileText className="w-4 h-4 text-primary" />
              <span className="font-medium">{group.agentName}</span>
              <span className="text-sm text-muted-foreground">v{group.versions[0]?.version}</span>
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>{group.versions.length} version{group.versions.length !== 1 ? 's' : ''}</span>
              {group.versions[0]?.metrics?.requestCount > 0 && (
                <span>{group.versions[0].metrics.requestCount} requests</span>
              )}
            </div>
          </button>

          {group.expanded && (
            <div className="border-t border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/30">
                    <th className="px-4 py-2 text-left font-medium">Version</th>
                    <th className="px-4 py-2 text-left font-medium">Hash</th>
                    <th className="px-4 py-2 text-left font-medium">Changed</th>
                    <th className="px-4 py-2 text-right font-medium">Requests</th>
                    <th className="px-4 py-2 text-right font-medium">Avg Cost</th>
                    <th className="px-4 py-2 text-center font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {group.versions.map(v => (
                    <tr key={v.version} className="border-t border-border/50 hover:bg-muted/20">
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1">
                          <Hash className="w-3 h-3 text-muted-foreground" />
                          <span className="font-mono">{v.version}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">
                        {v.promptHash.slice(0, 12)}...
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex items-center gap-1 text-muted-foreground">
                          <Clock className="w-3 h-3" />
                          {new Date(v.changedAt).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-right">{v.metrics.requestCount}</td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center gap-1 justify-end text-muted-foreground">
                          <DollarSign className="w-3 h-3" />
                          {v.metrics.avgCostUsd.toFixed(4)}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => viewVersion(v.agentName, v.version)}
                          className="text-primary hover:underline text-xs"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ))}

      {/* Version detail modal */}
      {selectedVersion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background border border-border rounded-lg shadow-lg max-w-3xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h4 className="font-semibold">
                {selectedVersion.agentName} v{selectedVersion.version}
              </h4>
              <button
                onClick={() => setSelectedVersion(null)}
                className="text-muted-foreground hover:text-foreground"
              >
                Close
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1">
              <div className="text-xs text-muted-foreground mb-2">
                Hash: {selectedVersion.promptHash} | Changed: {new Date(selectedVersion.changedAt).toLocaleString()} | By: {selectedVersion.changedBy}
              </div>
              <pre className="bg-muted/30 border border-border rounded p-4 text-xs whitespace-pre-wrap font-mono overflow-auto max-h-[60vh]">
                {selectedVersion.promptText}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
